import {
  askProviderLabel,
  normalizeAskProvider,
} from './askProviderStorage';

const EXTERNAL_ASK_CHAT_STORAGE_KEY = 'hashely_external_ask_chat_by_prompt';
const EXTERNAL_ASK_CHAT_MAX = 100;

function normalizePromptKey(prompt) {
  return String(prompt || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function externalAskStorageKey(provider, prompt) {
  return `${normalizeAskProvider(provider)}::${normalizePromptKey(prompt)}`;
}

function readExternalAskChatMap() {
  try {
    const raw = window.localStorage?.getItem(EXTERNAL_ASK_CHAT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeExternalAskChatMap(map) {
  try {
    const entries = Object.entries(map || {});
    entries.sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0));
    const pruned = Object.fromEntries(entries.slice(0, EXTERNAL_ASK_CHAT_MAX));
    window.localStorage?.setItem(EXTERNAL_ASK_CHAT_STORAGE_KEY, JSON.stringify(pruned));
  } catch {
    // ignore storage errors
  }
}

export function rememberExternalAskConversation(provider, prompt, conversationUrl) {
  const trimmedPrompt = String(prompt || '').trim();
  const url = String(conversationUrl || '').trim();
  if (!trimmedPrompt || !url) return;
  const key = externalAskStorageKey(provider, trimmedPrompt);
  const map = readExternalAskChatMap();
  map[key] = {
    provider: normalizeAskProvider(provider),
    prompt: trimmedPrompt,
    conversationUrl: url,
    updatedAt: Date.now(),
  };
  writeExternalAskChatMap(map);
}

export function findCachedExternalAskUrl(provider, prompt) {
  const key = externalAskStorageKey(provider, prompt);
  if (!normalizePromptKey(prompt)) return '';
  const entry = readExternalAskChatMap()[key];
  return String(entry?.conversationUrl || '').trim();
}

/**
 * True only when the extension never received OPEN_EXTERNAL_ASK.
 * "Message port closed" means the async background listener already started
 * opening Gemini — a URL fallback would add a second empty window.
 */
function shouldFallbackExternalAskToUrl(lastError, response) {
  if (lastError) {
    const message = String(lastError.message || '');
    if (/message port closed/i.test(message)) return false;
    return /could not establish connection|receiving end does not exist|invalid extension/i.test(message);
  }
  return Boolean(response && typeof response === 'object' && response.ok === false);
}

function stableWindowName(provider, prompt) {
  const id = normalizeAskProvider(provider);
  const key = normalizePromptKey(prompt);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return `hashely-${id}-${Math.abs(hash)}`;
}

const namedExternalWindows = new Map();

function openCenteredWindow(url, windowName = 'hashely-external') {
  const screenW = window.screen?.availWidth || window.screen?.width || 1200;
  const screenH = window.screen?.availHeight || window.screen?.height || 800;
  const width = Math.round(screenW * 0.85);
  const height = Math.round(screenH * 0.85);
  const left = Math.round((screenW - width) / 2);
  const top = Math.round((screenH - height) / 2);
  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  const popup = window.open(url, windowName, features);
  if (!popup) {
    return window.open(url, '_blank', 'noopener,noreferrer');
  }
  return popup;
}

/**
 * Focus an existing named popup if this page still holds a reference.
 * Do not use window.open('', name) — that creates a blank tab when no popup exists.
 */
function openOrFocusExternalWindow(url, windowName) {
  const existing = namedExternalWindows.get(windowName);
  if (existing && !existing.closed) {
    try {
      existing.focus();
      return existing;
    } catch {
      namedExternalWindows.delete(windowName);
    }
  }

  const popup = openCenteredWindow(url, windowName);
  if (popup && !popup.closed) {
    namedExternalWindows.set(windowName, popup);
  }
  return popup;
}

export const DEFAULT_GEMINI_LANGUAGE = 'en';

export const GEMINI_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: 'Korean' },
  { code: 'ja', label: 'Japanese' },
  { code: 'zh-CN', label: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: 'Chinese (Traditional)' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
  { code: 'pt-PT', label: 'Portuguese (Portugal)' },
  { code: 'ru', label: 'Russian' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'id', label: 'Indonesian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'pl', label: 'Polish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'sv', label: 'Swedish' },
  { code: 'da', label: 'Danish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'cs', label: 'Czech' },
  { code: 'ro', label: 'Romanian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'el', label: 'Greek' },
  { code: 'he', label: 'Hebrew' },
];

export function normalizeGeminiLanguage(language) {
  const input = String(language || '').trim();
  if (!input) return DEFAULT_GEMINI_LANGUAGE;
  if (GEMINI_LANGUAGE_OPTIONS.some(option => option.code === input)) {
    return input;
  }
  return DEFAULT_GEMINI_LANGUAGE;
}

export function openGoogleSearch(query) {
  const trimmed = String(query || '').trim();
  if (!trimmed) return;
  const url = `https://www.google.com/search?q=${encodeURIComponent(trimmed)}`;
  openCenteredWindow(url, `hashely-google-search-${Date.now()}`);
}

export function buildExternalAskUrl(
  question,
  provider = 'gemini',
  { language = DEFAULT_GEMINI_LANGUAGE } = {}
) {
  const trimmed = String(question || '').trim();
  if (!trimmed) return '';
  const encoded = encodeURIComponent(trimmed);
  const id = normalizeAskProvider(provider);

  if (id === 'gpt') {
    return `https://chatgpt.com/?q=${encoded}`;
  }
  if (id === 'claude') {
    return `https://claude.ai/new?q=${encoded}`;
  }
  if (id === 'perplexity') {
    return `https://www.perplexity.ai/search?q=${encoded}`;
  }

  const normalizedLanguage = normalizeGeminiLanguage(language);
  return `https://gemini.google.com/app?prompt=${encoded}&autosubmit=true&hl=${encodeURIComponent(normalizedLanguage)}&hashely=1`;
}

/**
 * Open the selected external AI with a prefilled prompt (Gemini / GPT / Claude / Perplexity).
 * When a prior conversation URL exists for the same provider+question, reopen that instead of
 * creating another fresh prompt. Extension path captures/reuses conversation URLs; URL fallback
 * focuses an existing named window when still open.
 */
export function openExternalAsk(
  question,
  {
    provider = 'gemini',
    extensionId,
    extensionInstalled,
    language = DEFAULT_GEMINI_LANGUAGE,
  } = {}
) {
  const trimmed = String(question || '').trim();
  if (!trimmed) return;
  const id = normalizeAskProvider(provider);
  const freshUrl = buildExternalAskUrl(trimmed, id, { language });
  if (!freshUrl) return;

  const cachedUrl = findCachedExternalAskUrl(id, trimmed);
  const windowName = stableWindowName(id, trimmed);

  const openViaUrl = (url = cachedUrl || freshUrl) => {
    openOrFocusExternalWindow(url, windowName);
  };

  const canUseExtension =
    extensionInstalled
    && extensionId
    && window.chrome?.runtime?.sendMessage;

  if (!canUseExtension) {
    openViaUrl();
    return;
  }

  const normalizedLanguage = normalizeGeminiLanguage(language);
  window.chrome.runtime.sendMessage(
    extensionId,
    {
      type: 'OPEN_EXTERNAL_ASK',
      provider: id,
      prompt: trimmed,
      autosubmit: true,
      language: normalizedLanguage,
    },
    response => {
      if (shouldFallbackExternalAskToUrl(window.chrome.runtime.lastError, response)) {
        openViaUrl();
        return;
      }
      if (response?.conversationUrl) {
        rememberExternalAskConversation(id, trimmed, response.conversationUrl);
      }
    }
  );
}

export { askProviderLabel, normalizeAskProvider };

export function truncateActionLabel(text, max = 96) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

export function isActionLabelTruncated(text, max = 96) {
  return String(text || '').trim().length > max;
}
